// frontend/src/components/Requisitions/edit/components/RealAmountSection.jsx

import React from "react";

import {
  fmtDateTime,
  fmtMoney,
  getNiceDescDisplay,
  getProductLabel,
  getUnitLabel,
  labelFrom,
} from "../utils";

export default function RealAmountSection({
  items,
  products,
  units,
  descCache,
  currentTenderId,
  realAmountBase,
  realUnitById,
  handleRealUnitChange,
  handleRealUnitBlur,
  qtyOf,
  estimatedUnitOf,
  estimatedTotalOf,
  realRowTotalOf,
  realTotalSum,
  realAmountReason,
  onRealAmountReasonChange,
  busyRealAmount,
  disableRealAmountByAck,
  disableRealByQuoteDraft,
  onSaveRealTotals,
  realAmountLogs,
}) {
  return (
    <div className="mt-6 border rounded p-4 bg-amber-50">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          Admin — Capturar monto real por partida
        </h3>

        <div className="text-xs text-gray-600">
          Monto real total (actual): <strong>{fmtMoney(realAmountBase)}</strong>
        </div>
      </div>

      <div className="mt-3 space-y-3">
        <div className="border rounded bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left">Producto</th>
                <th className="px-2 py-2 text-left">Descripción</th>
                <th className="px-2 py-2 text-center">Cant.</th>
                <th className="px-2 py-2 text-center">Unidad</th>
                <th className="px-2 py-2 text-center">Monto Unitario estimado</th>
                <th className="px-2 py-2 text-center">Costo Total Estimado</th>
                <th className="px-2 py-2 text-center">Monto Real Unitario</th>
                <th className="px-2 py-2 text-center">Monto real (partida)</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td className="px-2 py-3 text-center text-gray-500" colSpan={8}>
                    No hay partidas.
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const descDisplay = getNiceDescDisplay({
                    row: item,
                    descCache,
                    tenderId: currentTenderId,
                  });

                  const qty = qtyOf(item);
                  const estimatedTotal = estimatedTotalOf(item);
                  const estimatedUnit = estimatedUnitOf(item);
                  const rawUnit = String(realUnitById?.[item.id] ?? "");
                  const autoRealRowTotal = realRowTotalOf(item);

                  return (
                    <tr key={item.id} className="border-t">
                      <td className="px-2 py-2">
                        {item.product_display ||
                          labelFrom(products, item.product, getProductLabel) ||
                          "—"}
                      </td>

                      <td className="px-2 py-2">{descDisplay}</td>

                      <td className="px-2 py-2 text-center">{item.quantity}</td>

                      <td className="px-2 py-2 text-center">
                        {item.unit_display ||
                          labelFrom(units, item.unit, getUnitLabel) ||
                          "—"}
                      </td>

                      <td className="px-2 py-2 text-center">
                        <input
                          type="text"
                          className="w-36 border rounded px-2 py-1 bg-gray-100"
                          value={qty > 0 ? Number(estimatedUnit).toFixed(2) : ""}
                          readOnly
                        />
                      </td>

                      <td className="px-2 py-2 text-center">
                        <input
                          type="text"
                          className="w-36 border rounded px-2 py-1 bg-gray-100"
                          value={
                            item.estimated_total === ""
                              ? ""
                              : Number(estimatedTotal).toFixed(2)
                          }
                          readOnly
                        />
                      </td>

                      <td className="px-2 py-2 text-center">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-36 border rounded px-2 py-1"
                          value={rawUnit}
                          onChange={(e) =>
                            handleRealUnitChange(item.id, e.target.value)
                          }
                          onBlur={() => handleRealUnitBlur(item.id)}
                          placeholder="0.00"
                        />
                      </td>

                      <td className="px-2 py-2 text-center">
                        <input
                          type="text"
                          className="w-36 border rounded px-2 py-1 bg-gray-100"
                          value={
                            autoRealRowTotal === null
                              ? ""
                              : Number(autoRealRowTotal).toFixed(2)
                          }
                          readOnly
                          title="Calculado automáticamente: Cant. × Monto Real Unitario"
                          placeholder="Auto"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-gray-600">
          El <strong>Monto real (partida)</strong> se calcula automáticamente como
          <strong> Cant. × Monto Real Unitario</strong>.
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Monto Real Total (auto)
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 bg-gray-100"
              value={Number(realTotalSum).toFixed(2)}
              readOnly
            />
            <div className="text-[11px] text-gray-600 mt-1">
              Se calcula automáticamente (suma de montos reales por partida).
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Motivo del cambio (obligatorio)
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1"
              value={realAmountReason}
              onChange={(e) => onRealAmountReasonChange(e.target.value)}
              placeholder="Ej. Se capturó monto real unitario desde factura y se recalculó el total por partida"
            />
            <div className="text-[11px] text-amber-800 mt-1">
              Recomendación: verifica el costo real exacto antes de guardar.
            </div>
          </div>

          <div className="md:col-span-3 flex justify-end">
            <button
              type="button"
              disabled={
                busyRealAmount ||
                items.length === 0 ||
                disableRealAmountByAck ||
                disableRealByQuoteDraft
              }
              onClick={onSaveRealTotals}
              className={`px-4 py-2 rounded text-white ${
                busyRealAmount ||
                items.length === 0 ||
                disableRealAmountByAck ||
                disableRealByQuoteDraft
                  ? "bg-amber-300"
                  : "bg-amber-600 hover:bg-amber-700"
              }`}
              title={
                disableRealByQuoteDraft
                  ? "Hay una cotización seleccionada sin partidas marcadas (completa o quita el PDF)."
                  : disableRealAmountByAck
                    ? 'Primero marca la verificación del costo aproximado pero realista.'
                    : items.length === 0
                      ? "Para capturar monto real, primero registra al menos una partida."
                      : ""
              }
            >
              {busyRealAmount
                ? "Guardando..."
                : disableRealByQuoteDraft
                  ? "Guardar (cotización incompleta)"
                  : disableRealAmountByAck
                    ? "Guardar (verifica costo)"
                    : "Guardar montos reales (con auditoría)"}
            </button>
          </div>

          {disableRealByQuoteDraft && (
            <div className="md:col-span-3 text-[11px] text-amber-800 mt-1">
              Completa la cotización (marca partidas) o quita el PDF para poder
              capturar el monto real.
            </div>
          )}
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-700 mt-2 mb-2">
            Historial de cambios (total)
          </h4>

          {realAmountLogs.length === 0 ? (
            <div className="text-sm text-gray-600">
              Aún no hay cambios registrados.
            </div>
          ) : (
            <div className="border rounded bg-white overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Cuándo</th>
                    <th className="px-2 py-2 text-left">Quién</th>
                    <th className="px-2 py-2 text-center">Antes</th>
                    <th className="px-2 py-2 text-center">Después</th>
                    <th className="px-2 py-2 text-left">Por qué</th>
                  </tr>
                </thead>
                <tbody>
                  {realAmountLogs.map((log) => (
                    <tr key={log.id} className="border-t">
                      <td className="px-2 py-2">{fmtDateTime(log.changed_at)}</td>
                      <td className="px-2 py-2">
                        {log.changed_by_email || log.changed_by || "—"}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {fmtMoney(log.old_value)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {fmtMoney(log.new_value)}
                      </td>
                      <td className="px-2 py-2">{log.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}